import axios from 'axios';

/** Base da API Django (chat, projetos, etc.). Um único lugar para REACT_APP_API_URL. */
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.giobrain.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});

function safePath(url) {
  if (!url) return '';
  try {
    // Remove query (pode conter tokens) — só pathname
    const withoutOrigin = url.replace(/^https?:\/\/[^/]+/i, '');
    return withoutOrigin.split('?')[0] || withoutOrigin;
  } catch (_) {
    return String(url).split('?')[0];
  }
}

function reportApiTiming({ method, path, status, durationMs, canceled }) {
  if (typeof window === 'undefined') return;
  // Sample ~15% em produção para não saturar o dataLayer
  const sampleRate = process.env.NODE_ENV === 'production' ? 0.15 : 1;
  if (Math.random() > sampleRate) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'api_timing',
    api_method: method,
    api_path: path,
    api_status: status,
    api_duration_ms: durationMs,
    api_canceled: Boolean(canceled),
  });
}

api.interceptors.request.use(
  (config) => {
    config.metadata = { ...(config.metadata || {}), startedAt: Date.now() };
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    const startedAt = response.config?.metadata?.startedAt;
    if (startedAt) {
      reportApiTiming({
        method: (response.config.method || 'get').toUpperCase(),
        path: safePath(response.config.url),
        status: response.status,
        durationMs: Date.now() - startedAt,
        canceled: false,
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const startedAt = originalRequest.metadata?.startedAt;
    if (startedAt) {
      const canceled = Boolean(error.code === 'ERR_CANCELED' || error.message === 'canceled');
      reportApiTiming({
        method: (originalRequest.method || 'get').toUpperCase(),
        path: safePath(originalRequest.url),
        status: error.response?.status || 0,
        durationMs: Date.now() - startedAt,
        canceled,
      });
    }

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { supabase } = await import('./supabase');
      if (supabase) {
        const { data: { session } } = await supabase.auth.refreshSession();
        if (session?.access_token) {
          localStorage.setItem('access_token', session.access_token);
          originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
          processQueue(null, session.access_token);
          return api(originalRequest);
        }
      }
    } catch (refreshError) {
      processQueue(refreshError, null);
    } finally {
      isRefreshing = false;
    }

    localStorage.removeItem('access_token');
    return Promise.reject(error);
  }
);

export default api;
